/**
 * Talent review: the Annual I&G Review dashboard.
 *
 * HR set out the views and exports in its requirements deck of 25 Sep 2026 and
 * settled the definitions on 9 Oct 2026. It shows what the review CONCLUDED:
 * what the chairs published when they completed their sessions. A session still
 * open adds nothing until it completes, and the page says how many are open.
 *
 * One read of the people in scope feeds every view; the filters apply to every
 * view and every download. Views 3 to 5 (bench strength, succession coverage,
 * critical roles) add a second read, the approved positions per role, fetched
 * when one of them is first opened.
 *
 * Administrators and HR administrators only; the server enforces it.
 */
import React, { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, FileSpreadsheet, Loader2, ShieldAlert } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  talentReportingService,
  type ReportingActionsResponse,
  type ReportingPerson,
  type TalentReportingApiError,
} from '@/services/api/talentReportingService'
import { TalentFilterBar } from './TalentFilterBar'
import { NineBoxView } from './NineBoxView'
import { HipoView } from './HipoView'
import { KeyPeopleView } from './KeyPeopleView'
import { BenchView } from './BenchView'
import { SuccessionView } from './SuccessionView'
import { CriticalRolesView } from './CriticalRolesView'
import { Panel } from './TalentDashboardParts'
import {
  EMPTY_FILTERS,
  FILTER_FIELDS,
  activeFilterCount,
  applyFilters,
  filterOptions,
  type FilterKey,
  type TalentFilters,
} from './talentDashboardData'
import {
  applyRoleFilters,
  coverageRows,
  leaderRoleKeys,
  readyNowByRole,
  roleFilterOptions,
  successorsByRole,
  type RoleCoverage,
} from './talentRoleData'
import { actionsExportRows, downloadSheet, employeeReportRows, roleExportRows, sessionsExportRows } from './talentExports'

type ViewId = 'grid' | 'hipo' | 'bench' | 'succession' | 'critical' | 'key'

const VIEWS: { id: ViewId; label: string }[] = [
  { id: 'grid', label: '1. Talent profile / 9-box' },
  { id: 'hipo', label: '2. HIPO profile & growth' },
  { id: 'bench', label: '3. Bench strength' },
  { id: 'succession', label: '4. Succession coverage' },
  { id: 'critical', label: '5. Critical roles' },
  { id: 'key', label: '6. Key people' },
]

const ROLE_VIEWS: ViewId[] = ['bench', 'succession', 'critical']

const FIRST_REVIEW_YEAR = 2026

/** The review a date belongs to: each year's review opens in September. */
export function defaultReviewYear(today = new Date()): number {
  const year = today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1
  return Math.max(year, FIRST_REVIEW_YEAR)
}

function sast(iso: string): string {
  return new Date(iso).toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function TalentDashboard() {
  const queryClient = useQueryClient()
  const latest = defaultReviewYear()
  const years = useMemo(() => {
    const out: number[] = []
    for (let y = latest; y >= FIRST_REVIEW_YEAR; y--) out.push(y)
    return out
  }, [latest])
  const [year, setYear] = useState(latest)
  const [view, setView] = useState<ViewId>('grid')
  const [filters, setFilters] = useState<TalentFilters>(EMPTY_FILTERS)
  const [busy, setBusy] = useState<string | null>(null)

  const peopleQuery = useQuery({
    queryKey: ['talent-reporting', 'people', year],
    queryFn: () => talentReportingService.getPeople(year),
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => (err as TalentReportingApiError)?.status !== 403 && count < 2,
  })
  const people = peopleQuery.data?.people ?? []
  const filtered = useMemo(() => applyFilters(people, filters), [people, filters])
  const sessions = peopleQuery.data?.sessions
  // A server older than the grant only ever answered administrators.
  const viewer = peopleQuery.data?.viewer ?? { admin: true, areas: [] }

  // Views 3 to 5: the approved positions per role, read once a role view is opened.
  const roleView = ROLE_VIEWS.includes(view)
  const rolesQuery = useQuery({
    queryKey: ['talent-reporting', 'roles', year],
    queryFn: () => talentReportingService.getRoles(year),
    enabled: roleView,
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => (err as TalentReportingApiError)?.status !== 403 && count < 2,
  })
  const allRoles = useMemo(() => rolesQuery.data?.roles ?? [], [rolesQuery.data])
  const succession = useMemo(() => rolesQuery.data?.succession ?? [], [rolesQuery.data])
  const roles = useMemo(() => applyRoleFilters(allRoles, filters), [allRoles, filters])
  const readyNow = useMemo(() => readyNowByRole(people, succession, allRoles), [people, succession, allRoles])
  const coverage = useMemo(() => coverageRows(roles, successorsByRole(succession, allRoles)), [roles, succession, allRoles])
  const leaderKeys = useMemo(() => leaderRoleKeys(people, succession, allRoles), [people, succession, allRoles])
  const positionsNote = rolesQuery.data?.positions_synced_at
    ? `Positions as at the last F&O positions sync, ${sast(rolesQuery.data.positions_synced_at)} SAST. Contractor and casual positions are left out.`
    : 'Positions as at the last F&O positions sync. Contractor and casual positions are left out.'

  // The filters offer what the people carry, and on a role view what the roles carry too.
  const options = useMemo(() => {
    const fromPeople = filterOptions(people, filters)
    if (!roleView || !allRoles.length) return fromPeople
    const fromRoles = roleFilterOptions(allRoles, filters)
    const out = {} as Record<FilterKey, string[]>
    for (const { key } of FILTER_FIELDS) {
      out[key] = [...new Set([...fromPeople[key], ...fromRoles[key]])].sort((a, b) =>
        a.localeCompare(b, 'en', { sensitivity: 'base', numeric: true }),
      )
    }
    return out
  }, [people, allRoles, filters, roleView])

  const actionsFor = () =>
    queryClient.fetchQuery<ReportingActionsResponse>({
      queryKey: ['talent-reporting', 'actions', year],
      queryFn: () => talentReportingService.getActions(year),
      staleTime: 5 * 60 * 1000,
    })

  const run = async (what: string, job: () => Promise<void>) => {
    setBusy(what)
    try {
      await job()
    } catch (e) {
      toast.error((e as Error)?.message || 'Could not build the spreadsheet')
    } finally {
      setBusy(null)
    }
  }

  const filteredNote = activeFilterCount(filters) ? '-filtered' : ''
  const downloadPeople = (rows: ReportingPerson[], what: string) =>
    run(what, async () => {
      const { actions } = await actionsFor()
      await downloadSheet(employeeReportRows(rows, actions), what, `I&G-Review-${year}-${what}${filteredNote}`)
    })
  const downloadEmployeeReport = () => downloadPeople(filtered, 'Employee-level report')
  const downloadRoles = (rows: RoleCoverage[], what: string) =>
    run(what, async () => {
      await downloadSheet(roleExportRows(rows, readyNow), what, `I&G-Review-${year}-${what}${filteredNote}`)
    })
  const downloadSessions = () =>
    run('Reviews scheduled', async () => {
      const { sessions: rows } = await talentReportingService.getSessions(year)
      await downloadSheet(sessionsExportRows(rows), 'Reviews scheduled', `I&G-Review-${year}-Reviews-scheduled`)
    })
  const downloadActions = () =>
    run('Actions and decisions', async () => {
      const { actions } = await actionsFor()
      const ids = new Set(filtered.map((p) => p.employee_id))
      const rows = activeFilterCount(filters) ? actions.filter((a) => ids.has(a.employee_id)) : actions
      await downloadSheet(actionsExportRows(rows), 'Actions and decisions', `I&G-Review-${year}-Actions-and-decisions${filteredNote}`)
    })

  const error = peopleQuery.error as TalentReportingApiError | null

  return (
    <div className="mx-auto max-w-[1180px] space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Impact &amp; Growth Platform</div>
          <h1 className="font-serif text-3xl leading-tight text-heritage-blue">Talent review</h1>
          <p className="mt-1 max-w-[66ch] text-sm text-slate-500">
            What the Annual I&amp;G Review concluded, for every permanent and fixed-term employee in post, interns
            included. Results count once a session is completed.
          </p>
          {peopleQuery.data && !viewer.admin ? (
            <p className="mt-1 text-sm text-slate-600">
              Showing {viewer.areas.length ? viewer.areas.join(', ') : 'the whole organisation'}, as your access allows.
            </p>
          ) : null}
          {peopleQuery.data ? (
            <span className="mt-2.5 inline-flex flex-wrap items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-[11.5px] text-gray-600">
              <span className={cn('inline-block h-[7px] w-[7px] rounded-full', sessions?.open ? 'bg-amber-500' : 'bg-emerald-500')} />
              As at {sast(peopleQuery.data.generated_at)} SAST · {sessions?.completed ?? 0} of {sessions?.total ?? 0} sessions completed
              {sessions?.open ? `; ${sessions.open} still open, so their people are not on the grid yet` : ''}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setFilters(EMPTY_FILTERS) }}>
            <SelectTrigger className="h-8 w-[7.5rem] text-xs" aria-label="Review year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{`${y} review`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <Card>
          <CardContent className="flex items-start gap-3 p-6">
            {error.status === 403 ? (
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            ) : (
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            )}
            <div className="text-sm text-slate-600">
              {error.status === 403
                ? 'The talent dashboard is open to administrators, HR administrators and people given access to it.'
                : error.detail || 'The dashboard could not be loaded. Refresh to try again.'}
            </div>
          </CardContent>
        </Card>
      ) : peopleQuery.isLoading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading the review for everyone in scope…
        </div>
      ) : (
        <>
          {viewer.admin ? (
          <div className="flex flex-wrap items-center gap-2" aria-label="Excel exports">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Excel</span>
            {[
              { what: 'Reviews scheduled', go: downloadSessions, hint: 'Every session of the review: date, chair, who was invited, lists and placements in' },
              { what: 'Employee-level report', go: downloadEmployeeReport, hint: 'A row per person in scope, with the filters applied' },
              { what: 'Actions and decisions', go: downloadActions, hint: 'Everything the rooms recorded, with who answers for each action' },
            ].map(({ what, go, hint }) => (
              <Button key={what} variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={go} disabled={!!busy} title={hint}>
                {busy === what ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                {what}
              </Button>
            ))}
          </div>
          ) : null}

          <TalentFilterBar filters={filters} options={options} onChange={setFilters} />

          <div className="flex flex-wrap gap-1.5 border-b border-gray-200 pb-2" role="tablist" aria-label="Views">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={view === v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  view === v.id ? 'bg-heritage-blue text-white' : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>

          {view === 'grid' && <NineBoxView people={filtered} onExport={viewer.admin ? downloadPeople : undefined} />}
          {view === 'hipo' && (
            <HipoView people={filtered} onExport={viewer.admin ? downloadPeople : undefined} showDemographics={viewer.admin} />
          )}
          {view === 'key' && (
            <KeyPeopleView people={filtered} onExport={viewer.admin ? downloadPeople : undefined} showDemographics={viewer.admin} />
          )}
          {roleView && rolesQuery.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Counting the approved positions per role…
            </div>
          ) : roleView && rolesQuery.error ? (
            <Panel title="Positions per role">
              <p className="text-sm text-slate-600">
                {(rolesQuery.error as TalentReportingApiError).detail || 'The positions per role could not be loaded. Refresh to try again.'}
              </p>
            </Panel>
          ) : (
            <>
              {view === 'bench' && (
                <BenchView
                  roles={roles}
                  readyNow={readyNow}
                  positionsNote={positionsNote}
                  onExport={viewer.admin ? downloadRoles : undefined}
                />
              )}
              {view === 'succession' && (
                <SuccessionView
                  rows={coverage.filter((c) => leaderKeys.has(c.key))}
                  positionsNote={positionsNote}
                  onExport={viewer.admin ? downloadRoles : undefined}
                />
              )}
              {view === 'critical' && (
                <CriticalRolesView
                  rows={coverage.filter((c) => c.role.critical)}
                  holders={filtered.filter((p) => p.critical_role)}
                  positionsNote={positionsNote}
                  onExport={viewer.admin ? downloadRoles : undefined}
                  showDemographics={viewer.admin}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
